import * as React from "react"
import * as Y from 'yjs'

import { fromUint8Array, toUint8Array } from 'js-base64'

import type { Channel } from "@/utils/custom-types";
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"

import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Placeholder, Selection } from "@tiptap/extensions"
import { Toolbar } from "@/components/tiptap-ui-primitive/toolbar"

import FileHandler from '@tiptap/extension-file-handler'
import { ImageUploadNode } from "@/components/tiptap-node/image-upload-node/image-upload-node-extension"
import { HorizontalRule } from "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import { LinkWithCmdClick } from "@/components/tiptap-node/link-node/link-click-extension"
import { MAX_FILE_SIZE, sanitizeUrl } from "@/lib/tiptap-utils"
import { useChannelLinkHandler } from "@/hooks/use-channel-link-handler"
import { MainToolbarContent } from "../tiptap-templates/simple/simple-editor";
import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/utils/trpc";
import { useSubscription } from "@trpc/tanstack-react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as awarenessProtocol from 'y-protocols/awareness.js'

import { ySyncPluginKey, absolutePositionToRelativePosition } from 'y-prosemirror'
import { ServerWsEvent, type ServerEventMap } from "server/websocket-events";
import { toast } from "sonner";
import { AssetImage } from "../asset-image";
import { getDocDraft, setDocDraft } from "@/contexts/doc-scroll-store";


const userColors = [
	'#f44336', // red
	'#e91e63', // pink
	'#9c27b0', // purple
	'#673ab7', // deep purple
	'#3f51b5', // indigo
	'#2196f3', // blue
	'#03a9f4', // light blue
	'#00bcd4', // cyan
	'#009688', // teal
	'#4caf50', // green
	'#8bc34a', // light green
	'#ff9800', // orange
	'#ff5722', // deep orange
];

function getUserColor(userId: string): string {
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		hash = userId.charCodeAt(i) + ((hash << 5) - hash);
	}
	return userColors[Math.abs(hash) % userColors.length];
}

export interface DocViewProps {
	workspaceId: string;
	channelId?: string;
	channel?: Channel | null;
	session?: any;
	currentWorkspace?: any;
}

export const DocView: React.FC<DocViewProps> = ({
	workspaceId,
	channelId,
	channel,
	session,
	currentWorkspace,
}) => {
	const toolbarRef = useRef<HTMLDivElement>(null)
	const { openLinkInApp } = useChannelLinkHandler();
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const hasSetInitialScrollRef = useRef(false);
	const saveScrollRafRef = useRef<number | null>(null);
	const restoreScrollRafRef = useRef<number | null>(null);
	const draftKey = channelId ? String(channelId) : null;
	const [editorReady, setEditorReady] = useState(false);
	const [containerMounted, setContainerMounted] = useState(false);

	const scrollContainerCallbackRef = (node: HTMLDivElement | null) => {
		scrollContainerRef.current = node;
		setContainerMounted(!!node);
	};

	const queryClient = useQueryClient();
	const docStateQueryKey = trpc.doc.getDocState.queryKey({
		channelId: Number(channelId),
	});

	const updateDocState = useMutation(
		trpc.doc.updateByChannel.mutationOptions(),
	);

	const updateDocStateWebSocket = useMutation(
		trpc.doc.updateDocStateWebSocket.mutationOptions(),
	);

	const updateAwarenessWebSocket = useMutation(
		trpc.doc.updateAwarenessWebSocket.mutationOptions(),
	);

	const createPresignedImageUploadMutation = useMutation(
		trpc.doc.createPresignedImageUpload.mutationOptions(),
	);

	const validChannel = channel?.type === "doc";

	const { data: docState, isLoading: docStateLoading } = useQuery({
		...trpc.doc.getDocState.queryOptions({
			channelId: Number(channelId),
		}),
		enabled: validChannel,
		staleTime: 0,                    
		refetchOnMount: 'always',       
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	const ydoc = useMemo(() => new Y.Doc(), [channelId])
	const awareness = useMemo(() => new awarenessProtocol.Awareness(ydoc), [channelId]);

	const pendingAwListRef = useRef<Array<{ svB64: string; update: Uint8Array }>>([]);

	const prevUpdateStateRef = useRef<Uint8Array | null>(null);
	const updateStateRef = useRef<Uint8Array | null>(null);

	const DEBOUNCE_MS = 5000;

	const userColor = useMemo(() => getUserColor(session.user.id), [session.user.id]);

	useEffect(() => {
		awareness.setLocalState({
			user: { name: session.user.name, color: userColor, id: session.user.id },
			cursor: null,
		});

		return () => {
			awareness.setLocalState(null);
		}
	}, [awareness, userColor, session.user.id]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			awarenessProtocol.removeAwarenessStates(
				awareness,
				[ydoc.clientID],
				'window unload'
			);
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [awareness, ydoc]);

	useEffect(() => {
		if (docState) {
			const updateB64 = docState;
			const update = toUint8Array(updateB64)
			queueMicrotask(() => {
				Y.applyUpdate(ydoc, update, "remote");
			});
		}
	}, [docState, ydoc])

	const handleImageUpload = async (
		file: File,
		onProgress?: (event: { progress: number }) => void,
		abortSignal?: AbortSignal
	): Promise<string> => {
		if (!file) {
			throw new Error("No file provided")
		}
	
		if (file.size > MAX_FILE_SIZE) {
			toast.error(`File size exceeds maximum allowed (${MAX_FILE_SIZE / (1024 * 1024)}MB)`);
			throw new Error(
				`File size exceeds maximum allowed (${MAX_FILE_SIZE / (1024 * 1024)}MB)`
			)
		}

		if (abortSignal?.aborted) throw new Error("Upload cancelled");

		onProgress?.({ progress: 10 });

		try {
			const res = await createPresignedImageUploadMutation.mutateAsync({
			  channelId: Number(channelId),
			  fileName: file.name,
			  contentType: file.type || "application/octet-stream",
			  size: file.size,
			});
		  
			onProgress?.({ progress: 30 });

			const putRes = await fetch(res.uploadUrl, {
				method: "PUT",
				headers: {
					"Content-Type": file.type || "application/octet-stream",
				},
				body: file,
				signal: abortSignal,
			})

			if (!putRes.ok) {
				throw new Error(`Storage upload failed (${putRes.status})`);
			}
		  
			onProgress?.({ progress: 100 });
  			
			// console.log("res.src:", res.src);
			return res.src;
		} catch (err) {
			toast.error("Failed to upload image");
			const message =
			  err instanceof Error ? err.message : "Failed to upload image";
		  
			throw new Error(message);
		}
	}

	const editor = useEditor({
		onCreate: () => {
			// console.log("[doc-view] editor created");
			setEditorReady(true);
		},
		onSelectionUpdate({ editor }) {
			const state = editor.state

			const ystate = ySyncPluginKey.getState(state)
			if (!ystate) return;

			const { yXmlFragment, mapping } = ystate

			const { from, to } = state.selection

			const anchor = absolutePositionToRelativePosition(from, yXmlFragment, mapping);
			const head = absolutePositionToRelativePosition(to, yXmlFragment, mapping);

			awareness.setLocalStateField('cursor', { anchor, head })
		},
		immediatelyRender: false,
		shouldRerenderOnTransaction: false,
		editorProps: {
		  attributes: {
			autocomplete: "off",
			autocorrect: "off",
			autocapitalize: "off",
			"aria-label": "Main content area, start typing to enter text.",
			class: "simple-editor",
		  },
		  transformPastedHTML(html) {
			return html.replace(/<img[^>]*>/gi, "");
		  },
		},
		extensions: [
		  StarterKit.configure({
			horizontalRule: false,
			link: false, // Disable StarterKit's link, using custom LinkWithCmdClick instead
		  }),
		  LinkWithCmdClick.configure({
			openOnClick: false,
			onRegularClick: (href) => {
			  // Sanitize the URL first
			  const safeUrl = sanitizeUrl(href, window.location.href);
			  if (safeUrl === "#") return false;

			  // Try to open as in-app link (returns true if handled)
			  if (openLinkInApp(safeUrl, false)) {
				return true;
			  }

			  // External link - open in same tab
			  window.location.href = safeUrl;
			  return true;
			},
			onCmdClick: (href) => {
			  // Sanitize the URL first
			  const safeUrl = sanitizeUrl(href, window.location.href);
			  if (safeUrl === "#") return false;

			  // Try to open as in-app link in background (returns true if handled)
			  if (openLinkInApp(safeUrl, true)) {
				return true;
			  }

			  // External link - open in new tab
			  window.open(safeUrl, "_blank", "noopener,noreferrer");
			  return true;
			},
		  }),
		  Collaboration.configure({
			document: ydoc,
		  }),
		  CollaborationCaret.configure({
			// provider shim: only .awareness is required
			provider: { awareness } as any,
			user: {
			  name: session.user.name,
			  color: userColor,
			},
		  }),
		  Placeholder.configure({
			placeholder: 'Type here...',
		  }),
		  HorizontalRule,
		  TextAlign.configure({ types: ["heading", "paragraph"] }),
		  TaskList,
		  TaskItem.configure({ nested: true }),
		  Highlight.configure({ multicolor: true }),
		  AssetImage.configure({ allowBase64: false }),
		  Typography,
		  Superscript,
		  Subscript,
		  Selection,
		  ImageUploadNode.configure({
			accept: "image/*",
			maxSize: MAX_FILE_SIZE,
			limit: 3,
			upload: handleImageUpload,
			onError: (error) => console.error("Upload failed:", error),
		  }),
		  FileHandler.configure({
			// allowedMimeTypes: ['image/*'],
			onDrop: (editor, files, pos) => {
				const images = files.filter((f) => f.type.startsWith("image/"));
				if (images.length === 0) return false;
			
				// Fire-and-forget async (FileHandler expects a boolean return, not a Promise)
				(async () => {
				  for (const file of images) {
					const src = await handleImageUpload(file);
					editor
					  .chain()
					  .insertContentAt(pos, { type: "image", attrs: { src } })
					  .focus()
					  .run();
				  }
				})().catch((e) => console.error("[drop upload failed]", e));
			
				return true; // tells ProseMirror “handled”, prevents browser opening new tab
			},
			onPaste: (editor, files, htmlContent) => {
				const images = files.filter((f) => f.type.startsWith("image/"));
				if (images.length === 0) return false;
			
				const pos = editor.state.selection.anchor;
			
				(async () => {
				  for (const file of images) {
					const src = await handleImageUpload(file);
					editor
					  .chain()
					  .insertContentAt(pos, { type: "image", attrs: { src } })
					  .focus()
					  .run();
				  }
				})().catch((e) => console.error("[paste upload failed]", e));
			
				return true;
			},
		  })
		],
		// content
	}, [ydoc, channelId, userColor, openLinkInApp])

	useEffect(() => {
		hasSetInitialScrollRef.current = false;
		// console.log("[doc-view] useEffect reset editor ready");
		setEditorReady(false);
	}, [draftKey]);

	useLayoutEffect(() => {
		if (!draftKey) return;
		if (!docState) return;
		if (!editorReady || !editor) return;
		if (!containerMounted) return;

		const container = scrollContainerRef.current;
		if (!container) return;

		if (hasSetInitialScrollRef.current) return;

		if (restoreScrollRafRef.current != null) {
			cancelAnimationFrame(restoreScrollRafRef.current);
		}

		restoreScrollRafRef.current = requestAnimationFrame(() => {
			restoreScrollRafRef.current = null;
			if (hasSetInitialScrollRef.current) return;

			const savedScroll = getDocDraft(draftKey)?.scroll;
			if (!savedScroll) {
				hasSetInitialScrollRef.current = true;
				return;
			}

			let didApply = false;

			try {
				const docSize = editor.state.doc.content.size;
				if (docSize === 0) {
					return;
				}

				if (savedScroll.pos > docSize) {
					if (typeof savedScroll.fallbackScrollTop === "number") {
						container.scrollTop = savedScroll.fallbackScrollTop;
						didApply = true;
					}
					return;
				}

				const coords = editor.view.coordsAtPos(savedScroll.pos);
				const containerRect = container.getBoundingClientRect();
				const targetScrollTop =
					container.scrollTop +
					(coords.top - containerRect.top) -
					savedScroll.offsetPx;

				container.scrollTop = targetScrollTop;
				didApply = true;
			} catch {
				if (typeof savedScroll.fallbackScrollTop === "number") {
					container.scrollTop = savedScroll.fallbackScrollTop;
					didApply = true;
				}
			}

			if (didApply) {
				hasSetInitialScrollRef.current = true;
			}
		});

		return () => {
			if (restoreScrollRafRef.current != null) {
				cancelAnimationFrame(restoreScrollRafRef.current);
				restoreScrollRafRef.current = null;
			}
		};
	}, [draftKey, docState, editorReady, editor, containerMounted]);

	useEffect(() => {
		if (!draftKey) {
			// console.log("[doc-view] useEffect abort: no draftKey");
			return;
		}
		if (!editorReady || !editor) {
			// console.log("[doc-view] useEffect abort: editor not ready or editor missing");
			return;
		}
		if (!containerMounted) {
			// console.log("[doc-view] useEffect abort: container not mounted");
			return;
		}

		const container = scrollContainerRef.current;
		if (!container) {
			// console.log("[doc-view] useEffect abort: container not found");
			return;
		}

		const onScroll = () => {
			// console.log("[doc-view] onScroll");
			if (saveScrollRafRef.current != null) return;

			saveScrollRafRef.current = requestAnimationFrame(() => {
				saveScrollRafRef.current = null;
				const current = getDocDraft(draftKey) ?? {};
				const editorRect = editor.view.dom.getBoundingClientRect();
				const containerRect = container.getBoundingClientRect();
				const anchorTop = Math.max(containerRect.top + 8, editorRect.top + 8);
				const anchorLeft = editorRect.left + editorRect.width / 2;

				const pos = editor.view.posAtCoords({
					left: anchorLeft,
					top: anchorTop,
				});

				if (!pos) return;

				const coords = editor.view.coordsAtPos(pos.pos);
				const snapshot = {
					mode: "anchor" as const,
					pos: pos.pos,
					offsetPx: coords.top - containerRect.top,
					fallbackScrollTop: container.scrollTop,
				};

				// console.log("[doc-view] Saving scroll snapshot:", snapshot);
				setDocDraft(draftKey, { ...current, scroll: snapshot });
			});
		};

		container.addEventListener("scroll", onScroll, { passive: true });

		return () => {
			container.removeEventListener("scroll", onScroll);
			if (saveScrollRafRef.current != null) {
				cancelAnimationFrame(saveScrollRafRef.current);
				saveScrollRafRef.current = null;
			}
		};
	}, [draftKey, editorReady, editor, containerMounted]);

	function localHasAtLeastSender(ydoc: Y.Doc, senderSvB64?: string): boolean {
		if (!senderSvB64) return true;
	  
		const localMap  = Y.decodeStateVector(Y.encodeStateVector(ydoc));  
		const senderMap = Y.decodeStateVector(toUint8Array(senderSvB64));  
	  
		for (const [client, senderClock] of senderMap) {
		  const localClock = localMap.get(client) ?? 0;
		  if (localClock < senderClock) return false; 
		}
		return true; 
	}

	useSubscription(
		trpc.doc.subscribeToDocRedis.subscriptionOptions(
			{ docId: Number(channelId) },
			{
				onData: (event) => {
					if (event?.type === ServerWsEvent.DocUpdated) {
						const data = event.data as unknown as ServerEventMap[ServerWsEvent.DocUpdated];
						// console.log("[doc updated event data]", data);
						const updateB64 = data.update as string;
						if (!updateB64) return;

						const update = toUint8Array(updateB64);

						Y.applyUpdate(ydoc, update, "remote");

						// Update React Query cache with current full doc state
						const fullState = Y.encodeStateAsUpdate(ydoc);
						const fullStateB64 = fromUint8Array(fullState);
						queryClient.setQueryData(docStateQueryKey, fullStateB64);

						const keep: Array<{ svB64: string; update: Uint8Array }> = [];
						for (const item of pendingAwListRef.current) {
							if (data.stateVector === item.svB64) {
								awarenessProtocol.applyAwarenessUpdate(awareness, item.update, "remote");
							} else {
								keep.push(item); 
							}
						}
						pendingAwListRef.current = keep;
					} else if (event?.type === ServerWsEvent.AwarenessUpdated) {
						const data = event.data as unknown as ServerEventMap[ServerWsEvent.DocUpdated];
						// console.log("[awareness updated event data]", data);
						const updateB64 = data.update as string;
						if (!updateB64) return;

						const update = toUint8Array(updateB64);
						const svB64 = data.stateVector as string;
						
						const caughtUp = localHasAtLeastSender(ydoc, svB64);

						if (caughtUp) {
						  awarenessProtocol.applyAwarenessUpdate(awareness, update, "remote");
						} else {
						  pendingAwListRef.current.push({ svB64, update });
						}

					}
				},
				onError: (err: unknown) => {
					console.error("[channel event error]", err);
				},
			},
		),
	);

	function elementsEqual(prev: Uint8Array | null, cur: Uint8Array | null) {
		if (prev === cur) return true;         
		if (!prev || !cur) return false;     
		if (prev.length !== cur.length) return false;

		for (let i = 0; i < prev.length; i++) {
			if (prev[i] !== cur[i]) return false;
		}

		return true;
	}

	useEffect(() => {
		const onUpdate = (update: Uint8Array, origin: unknown) => {
			if (origin === "remote") return
			const updateB64 = fromUint8Array(update)
			const sv = Y.encodeStateVector(ydoc);
			const svB64 = fromUint8Array(sv);

			updateDocStateWebSocket.mutate({
				channelId: Number(channelId),
				update: updateB64,
				stateVector: svB64
			})

			// Update React Query cache with current full doc state
			const fullState = Y.encodeStateAsUpdate(ydoc);
			const fullStateB64 = fromUint8Array(fullState);
			queryClient.setQueryData(docStateQueryKey, fullStateB64);
		}

		const onAwarenessUpdate = (
			{ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
  			origin: unknown,
		) => {
			if (origin === "remote") return;

			const ids = [...added, ...updated, ...removed]
			if (!ids.length) return
			const awarenessUpdate = awarenessProtocol.encodeAwarenessUpdate(awareness, ids);
			const svB64 = fromUint8Array(Y.encodeStateVector(ydoc));

			updateAwarenessWebSocket.mutate({
				channelId: Number(channelId),
				update: fromUint8Array(awarenessUpdate),
				stateVector: svB64
			})
		}

		ydoc.on("update", onUpdate)
		awareness.on("update", onAwarenessUpdate)
	
		return () => {
			ydoc.off("update", onUpdate)
			awareness.off("update", onAwarenessUpdate)
		}
	}, [ydoc, updateDocState, channelId])
	
  
	if (validChannel && docStateLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
			</div>
		);
	}

	return (
	  <div className="simple-editor-wrapper" ref={scrollContainerCallbackRef}>
		<EditorContext.Provider value={{ editor }}>
		  <Toolbar
			ref={toolbarRef}
		  >
			<MainToolbarContent />
		  </Toolbar>
  
		  <EditorContent
			editor={editor}
			role="presentation"
			className="simple-editor-content"
		  />
		</EditorContext.Provider>
	  </div>
	)
};
